import { type ReactNode, type RefObject, useEffect, useRef } from 'react';
import Loader from '../Loader';

interface InfiniteScrollProps<T> {
  items: T[];
  renderItem: (item: T, index: number) => ReactNode;
  loadMore: () => void;
  hasMore: boolean;
  loading: boolean;
  /** Ref to the scrollable container. Used as the IntersectionObserver root so the
   *  sentinel is detected relative to the panel, not the viewport. */
  scrollRef?: RefObject<HTMLElement>;
  className?: string;
  loaderSize?: number;
}

function InfiniteScroll<T>({
  items,
  renderItem,
  loadMore,
  hasMore,
  loading,
  scrollRef,
  className,
  loaderSize = 30,
}: InfiniteScrollProps<T>) {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || loading) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadMore();
      },
      { root: scrollRef?.current ?? null, threshold: 0.1 }
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [hasMore, loading, loadMore, scrollRef]);

  return (
    <div className={className}>
      {items.map((item, i) => renderItem(item, i))}
      {hasMore && <div ref={sentinelRef} style={{ height: '1px' }} />}
      {loading && (
        <div className="py-2">
          <Loader size={loaderSize} dots />
        </div>
      )}
    </div>
  );
}

export default InfiniteScroll;
